import {
	getSubOrdersRequest,
	getOrderByIdRequest,
	getGeocodeSubOrdersRequest,
	getSubOrdersTotalsRequest,
	getSubOrdersByOrderIdsRequest,
	createParallelSubOrderRequest,
	getTripAddressByIdRequest
} from '../requests';
import searchFilterActions from './searchFilter/OrderSearch.actions.js';
import { cleanLocationTags, throwTranslatableError, newUUID, formatLocationTag } from '@/core';
import moment from 'moment';

function setValue({ commit }, { value, key }) {
	commit('setValue', { value, key });
}

function cleanSearchFilter(searchFilter) {
	return {
		searchSubOrders: true,
		planned: searchFilter.planned,
		date: searchFilter.date,
		dateType: searchFilter.dateType || 'etd',
		searchAreas: Array.isArray(searchFilter.searchAreas) ? cleanLocationTags(searchFilter.searchAreas) : null,
		fromDespatchParty: Array.isArray(searchFilter.fromDespatchParty) ? cleanLocationTags(searchFilter.fromDespatchParty) : null,
		toDeliveryParty: Array.isArray(searchFilter.toDeliveryParty) ? cleanLocationTags(searchFilter.toDeliveryParty) : null,
		customer: searchFilter.customer || [],
		goodsTypes: Array.isArray(searchFilter.goodsTypes) ? searchFilter.goodsTypes.map(x => x.tpxCode) : [],
		orderTypes: Array.isArray(searchFilter.orderTypes) ? searchFilter.orderTypes.map(x => x.r10Id) : [],
		hasAtd: searchFilter.hasAtd ? searchFilter.hasAtd.value : undefined,
		economicalStatus: Array.isArray(searchFilter.economicalStatus) ? searchFilter.economicalStatus : [],
		orderStatus: Array.isArray(searchFilter.orderStatus) ? searchFilter.orderStatus : [],
		references: Array.isArray(searchFilter.references) ? searchFilter.references.map (x => x.id) : [],
		referencesText: searchFilter.referencesText,
		waybillNumber: Array.isArray(searchFilter.waybillNumber) ? searchFilter.waybillNumber.map (x => x.id) : [],
		inboundEdi: searchFilter.mustHaveInboundEdi ? searchFilter.inboundEdi : null,
		mustHaveInboundEdi: searchFilter.mustHaveInboundEdi,
		mustHaveDeviation: searchFilter.mustHaveDeviation,
	};
}


async function fetchOrders({ dispatch, commit, state }) {
	commit('setValue', { value: true, key: 'ordersLoading' });
	commit('clearOrders');
	try {
		dispatch('fetchTotals');
		let currentSearch = newUUID();
		commit('setValue', { value: currentSearch, key: 'searchUUID' });
		commit('setValue', { value: true, key: 'ordersFetching' });
		let orders = (await getSubOrdersRequest(cleanSearchFilter(state.searchFilter))).sort((a,b) => b.orderNumber - a.orderNumber);
		commit('set', (state) => {
			state.orders.splice(0, 0, ...orders.splice(0,200));
		});
		commit('setValue', { value: false, key: 'ordersLoading' });

		await new Promise((resolve) => setTimeout(resolve, 200));
		if (state.searchUUID !== currentSearch)
			return;
		commit('set', (state) => {
			state.orders.splice(state.orders.length, 0, ...orders);
		});

		commit('setValue', { key: 'ordersLastUpdated', value: moment() });

	} catch (err) {
		throw new Error('failedToFetch');
	}
	commit('setValue', { value: false, key: 'ordersFetching' });
	commit('setValue', { value: false, key: 'ordersLoading' });
}

async function fetchTotals({ commit, state }) {
	try {
		let totals = await getSubOrdersTotalsRequest(cleanSearchFilter(state.searchFilter));
		commit('setTotals', totals);
	} catch (err) {
		console.error(err);
		throw new Error('failedToFetch');
	}
}

async function refreshOrders({ state, commit }, originalOrderIds) {
	commit('setValue', { value: true, key: 'ordersRefreshing' });
	try {
		let response = await getSubOrdersByOrderIdsRequest(originalOrderIds,cleanSearchFilter(state.searchFilter));
		commit('refreshSubOrdersFromOrderIds', { orders: response, originalOrderIds });
	} catch (err) {
		throw new Error('failedToRefresh');
	}
	commit('setValue', { value: false, key: 'ordersRefreshing' });
}

function updateOrdersWithPolledOrders({ state, commit }) {
	if (!state.polledOrders || state.polledOrders.length <= 0) return;
	if (!state.ordersLastUpdated || !moment.isMoment(state.ordersLastUpdated) ||
		!state.polledOrdersLastUpdated || !moment.isMoment(state.polledOrdersLastUpdated)) return;

	if (moment(state.polledOrdersLastUpdated).isAfter(state.ordersLastUpdated)) {
		commit('updateMatchingSubOrders', state.polledOrders);
		commit('setValue', { key: 'ordersLastUpdated', value: moment() });
	}
}

async function fetchOrdersInBackground({ commit, state }) {
	if (state.liveUpdateInProgress) return;
	commit('set', (state) => { state.liveUpdateInProgress = true; });
	// Force request to be allowed if no return after minute
	setTimeout(() => {
		commit('set', (state) => { state.liveUpdateInProgress = false; });
	}, 60000);
	try {
		let response = await getSubOrdersRequest(cleanSearchFilter(state.searchFilter));
		commit('setPolledOrders',  response);
		commit('setValue', { key: 'polledOrdersLastUpdated', value: moment() });
	} catch (err) {
		throw new Error('failedToFetch');
	}
	commit('set', (state) => { state.liveUpdateInProgress = false; });
}

function startPollingOrders({ commit, dispatch }) {
	commit('set', (state) => {
		state.pollInterval = setInterval(() => { dispatch('fetchOrdersInBackground'); }, 10000);
	});
}

async function stopPollingOrders({ commit }) {
	commit('set', (state) => { clearInterval(state.pollInterval); });
}

function loadSavedSearch({ commit }, savedSearch) {
	if (savedSearch) commit('loadSavedSearch', savedSearch);
}

async function setSelectedOriginalOrderBySubOrderId({ state, commit, dispatch }, ids) {
	if (!ids || !ids[0]) {
		commit('setSelectedOriginalOrder', {});
		return;
	}
	let lastOrderIndex =  ids.length - 1;
	let subOrder = state.orders.find(x => x.id === ids[lastOrderIndex]);
	if (!subOrder) {
		commit('setSelectedOriginalOrder', {});
		return;
	}

	dispatch('setSelectedOriginalOrderById', subOrder.orderId);
}

async function setSelectedOriginalOrderById({ commit }, id) {
	let order;
	if (id) {
		let response = await getOrderByIdRequest(id);
		if (response) {
			order = response;
		}
	}

	commit('setSelectedOriginalOrder', order);
}

async function geocodeOrder({ commit }, order) {
	commit('setValue', { value: true, key: 'geocoding' });
	try {
		let orders = await getGeocodeSubOrdersRequest([{ id: order.id }]);
		commit('refreshMatchingSubOrders', { orders, ids: [order.id] });
	} catch (err) {
		throw new Error('failedToGeocode');
	}
	commit('setValue', { value: false, key: 'geocoding' });
}

async function createParallelSubOrder({ dispatch }, subOrderId) {
	try {
		let order = await createParallelSubOrderRequest(subOrderId);
		dispatch('refreshOrders', [order.orderId]);
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToCreate');
	}
}

async function updateFitDespatchToTrip({ commit, state }, { fit, trip }) {
	let tags = state.searchFilter.fromDespatchParty.filter(x => !x.notRemovable);
	try {
		if (fit && trip && trip.fromAddress) {
			tags.push(formatLocationTag({ ...trip.fromAddress, type: 'address', notRemovable: true }));
			let tripAddress = await getTripAddressByIdRequest(trip.fromAddress.id);
			if (tripAddress && tripAddress.linkedLocations && tripAddress.linkedLocations.length > 0) {
				for (let tag of tripAddress.linkedLocations) {
					tags.push({
						...tag,
						notRemovable: true
					});
				}
			}
		}
		commit('setSearchFilterTagList', { key: 'fromDespatchParty', tagList: tags });
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

async function updateFitDeliveryToTrip({ commit, state }, { fit, trip }) {
	let tags = state.searchFilter.toDeliveryParty.filter(x => !x.notRemovable);
	try {
		if (fit && trip && trip.toAddress) {
			tags.push(formatLocationTag({ ...trip.toAddress, type: 'address', notRemovable: true }));

			let tripAddress = await getTripAddressByIdRequest(trip.toAddress.id);
			if (tripAddress && tripAddress.linkedLocations && tripAddress.linkedLocations.length > 0) {
				for (let tag of tripAddress.linkedLocations) {
					tags.push({
						...tag,
						notRemovable: true
					});
				}
			}
		}
		commit('setSearchFilterTagList', { key: 'toDeliveryParty', tagList: tags });
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}
export default {
	...searchFilterActions,
	setValue,
	fetchOrders,
	fetchTotals,
	refreshOrders,
	updateOrdersWithPolledOrders,
	fetchOrdersInBackground,
	startPollingOrders,
	stopPollingOrders,
	loadSavedSearch,
	setSelectedOriginalOrderBySubOrderId,
	setSelectedOriginalOrderById,
	geocodeOrder,
	createParallelSubOrder,
	updateFitDespatchToTrip,
	updateFitDeliveryToTrip
};
